/* global fbq */

import React, { useState, useEffect } from 'react';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StripeLogo from '../assets/Payment/stripe.svg';
import VisaLogo from '../assets/Payment/visa.svg';
import MastercardLogo from '../assets/Payment/mastercard.svg';
import AmericanExpressLogo from '../assets/Payment/amex.png';
import LPTooltip from './LPTooltip';
import { faEyeSlash, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';
import SignIn from '../content/NavBar/SignIn';

export default function CheckoutSummary({ order_details, price, checkoutInit, loadTimer, disableAddons, discountedPrice }) {
    const { getAccessToken } = useAuth();
    const currency = localStorage.getItem('currency') || 'USD';


    const [coupon, setCoupon] = useState('SAVE20');
    const [couponCode, setCouponCode] = useState('');
    const [discountPercent, setDiscountPercent] = useState(20);
    const [snackbar, setSnackbar] = useState({ open: true, severity: 'success', message: 'SAVE20 applied! 20% discount.' });
    const [addons, setAddons] = useState([]);
    const [showLogin, setShowLogin] = useState(false);

    const priceNum = Number(price) || 0;
    const fmt = (n) => n.toFixed(2);
    const isCouponApplied = Boolean(discountPercent && discountPercent > 0);
    const handleClose = () => {
        setSnackbar(s => ({ ...s, open: false }));
    };

    useEffect(() => {
        // Set the discounted price based on the discount percent
        if(discountPercent > 0 && priceNum > 0) {
            discountedPrice((priceNum * (1 - discountPercent / 100)).toFixed(2));
        } else {
            discountedPrice(priceNum.toFixed(2));
        }
    }, [discountPercent, priceNum]);

    const pushToDataLayer = (payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
    };

    useEffect(() => {
        if (!coupon || price == null) return;
        //if it is not loading, check the price and if it is less than 5 or 100, remove the coupon
        if(price !== null) {
            if(priceNum < 5 && currency === 'USD') {
                setDiscountPercent(0);
                setCoupon('');
                setSnackbar({ open: true, severity: 'info', message: 'Your order must be at least $5 USD to apply a coupon.' });
            }
            if(priceNum < 100 && currency === 'MXN') {
                setDiscountPercent(0);
                setCoupon('');
                setSnackbar({ open: true, severity: 'info', message: 'Your order must be at least $100 MXN to apply a coupon.' });
            }
        }
    }, [price]);


    useEffect(() => {
        //check if the order is division-boosting
        if( order_details.length === 0) return;
        switch (order_details[0][1]) {
            case 'Division Boosting':
                setAddons(['Appear Offline', 'VPN']);
                break;
            case 'Placement Games':
                setAddons(['Appear Offline', 'VPN']);
                break;
            case 'Net Wins':
                setAddons(['Appear Offline', 'VPN']);
                break;
            case 'Normal Games':
                setAddons(['Appear Offline', 'VPN']);
                break;
            default:
                setAddons([]);
                break;
        }
    }, [order_details]);

  const handleRemoveCoupon = () => {
    setDiscountPercent(0);
    setCoupon('');
    setCouponCode('');
    setSnackbar({ open: true, severity: 'info', message: 'Coupon removed.' });
    setTimeout(() => {
      setSnackbar({ open: false, severity: 'info', message: '' });
    }, 4000);
  };

  const handleApplyCoupon = async (code) => {
    try {
      if (getAccessToken()) {
        axios
          .get(
            `${process.env.REACT_APP_API_URL}/user/coupon-validator/${code.toUpperCase()}`,
            { headers: { Authorization: getAccessToken() } }
          )
          .then((response) => {
            setDiscountPercent(response.data);
            setCoupon(code);
            setSnackbar({ open: true, severity: 'success', message: `Coupon applied! ${response.data}% discount.` });
          })
          .catch((error) => {
            setDiscountPercent(0);
            setCoupon('');
            setSnackbar({ open: true, severity: 'error', message: 'Invalid or expired coupon.' });
          });
      }
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: 'Failed to apply coupon. Please try again.' });
    }
  };

  const handleCheckout = () => {
    if (priceNum > 0) {
        let priceWithDiscount = discountPercent > 0 ? (priceNum * (1 - discountPercent / 100)).toFixed(2) : priceNum.toFixed(2);
        if(!getAccessToken()) {
            setShowLogin(true);
            //generate lead
            if (typeof fbq !== 'undefined') {
                fbq('track', 'Lead', {
                    value: priceWithDiscount,
                    currency: currency,
                });
            }
            pushToDataLayer({
                event: 'sign_up',
                method: 'checkout_flow'
            });
        } else {
            checkoutInit({ addons, price: priceWithDiscount, coupon });
        }
    } else {
        if(!getAccessToken()) {
            setShowLogin(true);
            return;
        }
        setSnackbar({ open: true, severity: 'error', message: 'Please select a service to checkout.' });
    }
  }

  return (
    <div className="w-full lg:w-[320px] flex-shrink-0 scroll-mt-[80px]" id="checkout-summary">
        <div className="sticky top-[92px] z-20 relative rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] p-6 text-white shadow-sm">
            {/* Main content */}
            <h2 className="mb-4 text-lg font-semibold text-[#00BAFF]">Checkout Summary</h2>
            <div className="space-y-3 text-sm text-[var(--secondary-text)] mb-4">
              {order_details.map(([label, value], i) => (
                <div key={i} className="flex justify-between">
                  <span className='text-[var(--secondary-text)]'>{label}</span>
                  <span className='text-white'>{value}</span>
                </div>
              ))}
            </div>
            <hr className="my-3 border-[var(--border)]" />

            {/* Sub‑total */}
            <div className="mb-4 flex justify-between text-sm font-semibold">
            <span className="text-[var(--secondary-text)]">Subtotal</span>
            <span>${fmt(priceNum)} {currency}</span>
            </div>

            {/* Coupon input */}
            <div className={`relative flex gap-4 ${disableAddons? 'mb-4' : 'mb-3'}`}>
                {/* input */}
                <label
                htmlFor="coupon"
                className={`
                pointer-events-none absolute left-3
                -translate-y-1/2 bg-[var(--bg-surface)] px-1 text-xs text-gray-400
                transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm
                
                ${isCouponApplied ? 'text-green-300' : 'text-gray-400'}
                peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs
                z-10
                `}
            >
            Coupon&nbsp;Code
                </label>
                <input
                id="coupon"
                type="text"
                placeholder=""
                className={`
                h-10 w-full rounded-md border bg-[var(--bg-surface)] px-3 text-sm
                focus:outline-none focus:ring-1 focus:ring-[var(--focus-ring)]
                ${isCouponApplied
                ? 'border-green-300 text-green-200 cursor-not-allowed'
                : 'border-[var(--border)] text-white placeholder-[var(--secondary-text)]'}
                `}
                value={isCouponApplied ? coupon : couponCode}
                disabled={isCouponApplied}
                onChange={(e) => setCouponCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon(couponCode)}
                />
                {/* button */}
                {isCouponApplied ? (
                <button
                    onClick={handleRemoveCoupon}
                    className="h-10 rounded-md border border-red-800 bg-red-700 px-3 text-sm font-semibold text-white transition hover:bg-red-800"
                >
                Remove
                </button>
                ) : (
                <button
                    onClick={() => handleApplyCoupon(couponCode)}
                    className="h-10 rounded-md bg-[var(--blue)] px-3 text-sm font-semibold text-white transition hover:bg-[#00a3e0]"
                >
                Apply
                </button>
                )}
            </div>

            {/* Free add‑ons */}
            {!disableAddons&&<div className="mb-4">
            <h3 className="mb-2 text-sm font-semibold text-[var(--secondary-text)]">Free Add-ons</h3>
            <div className="space-y-4">
                {[
                ['Appear Offline', "Your friendlist won't notice you're online while being boosted.", faEyeSlash],
                ['VPN', 'Booster will use a VPN to match your country’s IP for extra account safety.', faShieldHalved],
                ].map(([label, desc, icon], i) => {
                const isChecked = addons.includes(label);
                return (
                    <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={icon} fixedWidth className="w-4 h-4 mr-2 text-[#00BAFF]" />
                        <label className="text-sm text-white mr-2">{label}</label>
                        <span className="text-sm text-[#00BAFF] mr-2">FREE</span>
                        <LPTooltip tooltipText={desc} />
                    </div>
                    <input
                        type="checkbox"
                        className="toggle-switch"
                        checked={isChecked}
                        onChange={() => {
                        const next = isChecked
                            ? addons.filter(a => a !== label)
                            : [...addons, label];
                        setAddons(next);
                        }}
                    />
                    </div>
                );
                })}
            </div>
            </div>}

            <hr className="my-2 border-[var(--border)]" />

            {/* Total with strike‑through original */}
            <div className="flex flex-col items-end text-base font-semibold">
            
            <div className="flex justify-between w-full">
                <span className="text-md text-white">Total</span>
                <span>
                ${fmt(priceNum * (1 - discountPercent / 100))} {currency}
                {isCouponApplied && (
                <span className="mb-1 line-through text-xs text-[var(--secondary-text)]">
                ${fmt(priceNum)}
                </span>
            )}
                </span>
                
            </div>
            </div>
            {/* CTA */}
            <button
                onClick={() => {priceNum > 0 ? handleCheckout() : setSnackbar({ open: true, severity: 'error', message: 'Please select a service to checkout.' })}}
                className="
                    mt-6 w-full rounded-full bg-[var(--blue)]
                    px-4 py-2 font-semibold flex items-center justify-center
                    shadow-[inset_0_0_0_2px_rgba(255,255,255,0.1)]
                    transition hover:bg-[#00a3e0]
                "
                >
                <FontAwesomeIcon icon={faLock} className="mr-2 text-white" />
                Secure Checkout
            </button>

            {/* Payment-method trust bar */}
            <div
                aria-label="Secure payment methods"
                className="
                    mt-4 flex flex-col items-center
                "
            >
                {/* Logos row */}
                <div className="flex items-center gap-4">
                    <img src={StripeLogo}     alt="Stripe – Secure payments" className="h-5" />
                    <img src={VisaLogo}       alt="Visa"                      className="h-5" />
                    <img src={MastercardLogo} alt="Mastercard"                className="h-5" />
                    <img src={AmericanExpressLogo} alt="American Express"     className="h-5" />
                </div>

                {/* Microcopy */}
                <p className="mt-1 text-[10px] text-gray-400 flex items-center gap-1">
                    <FontAwesomeIcon icon={faLock} className="w-3 h-3" />
                    256-bit SSL encryption • PCI-compliant payments
                </p>
            </div>

            {loadTimer && (
            <div className="absolute inset-0 z-30 flex items-center justify-center rounded-lg bg-black/25">
                <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-t-[#00BAFF] border-gray-700"
                aria-label="Loading spinner"
                />
            </div>
            )}
        </div>
        <SignIn open={showLogin} close={() => setShowLogin(false)}/>
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                {snackbar.message}
            </Alert>
        </Snackbar>
    </div>
  );
}
